import type { MainColorsType } from '~/types/components'
import type {
  AddressLabelConfigType,
  BankAccountTypeConfigType,
  CompanySettingsType,
  CurrencyConfigType,
  CustomerClassificationConfigType,
  CustomerStateConfigType,
  DocumentTypeConfigType,
  LocaleConfigType
} from '~/types/Settings'

// ─── Defaults del sistema ────────────────────────────────────────────────────
// Viven en `@florezjaramillo/florezjaramillo-core` (utils/company-settings): el
// backend las siembra en el seed con el mismo catálogo, así que añadir una
// moneda o una etapa del CRM se hace en un solo sitio.

import {
  DEFAULT_ADDRESS_LABELS,
  DEFAULT_BANK_ACCOUNT_TYPES,
  DEFAULT_CURRENCIES,
  DEFAULT_CUSTOMER_CLASSIFICATIONS,
  DEFAULT_CUSTOMER_STATES,
  DEFAULT_DOCUMENT_TYPES,
  DEFAULT_LOCALE,
  DEFAULT_COMPANY_SETTINGS as DEFAULT_SETTINGS
} from '@florezjaramillo/florezjaramillo-core'

export { DEFAULT_ADDRESS_LABELS, DEFAULT_BANK_ACCOUNT_TYPES, DEFAULT_CURRENCIES, DEFAULT_CUSTOMER_CLASSIFICATIONS, DEFAULT_CUSTOMER_STATES, DEFAULT_DOCUMENT_TYPES, DEFAULT_LOCALE, DEFAULT_SETTINGS }

// ─── Merge con defaults (settings viejos no deben dejar bloques incompletos) ─

export function mergeSettings(saved: Partial<CompanySettingsType> | null | undefined): CompanySettingsType {
  const s = saved ?? {}
  return {
    ...DEFAULT_SETTINGS,
    ...s,
    locale: { ...DEFAULT_SETTINGS.locale, ...(s.locale ?? {}) },
    quoteBranding: { ...DEFAULT_SETTINGS.quoteBranding, ...(s.quoteBranding ?? {}) },
    quoteIssuer: { ...DEFAULT_SETTINGS.quoteIssuer, ...(s.quoteIssuer ?? {}) },
    quoteDefaults: { ...DEFAULT_SETTINGS.quoteDefaults, ...(s.quoteDefaults ?? {}) },
    paymentInfo: { ...DEFAULT_SETTINGS.paymentInfo, ...(s.paymentInfo ?? {}) },
    currencies: Array.isArray(s.currencies) && s.currencies.length ? s.currencies : DEFAULT_CURRENCIES,
    documentTypes: Array.isArray(s.documentTypes) && s.documentTypes.length ? s.documentTypes : DEFAULT_DOCUMENT_TYPES,
    customerStates: Array.isArray(s.customerStates) && s.customerStates.length ? s.customerStates : DEFAULT_CUSTOMER_STATES,
    bankAccountTypes: Array.isArray(s.bankAccountTypes) && s.bankAccountTypes.length ? s.bankAccountTypes : DEFAULT_BANK_ACCOUNT_TYPES,
    addressLabels: Array.isArray(s.addressLabels) && s.addressLabels.length ? s.addressLabels : DEFAULT_ADDRESS_LABELS,
    customerClassifications: Array.isArray(s.customerClassifications) && s.customerClassifications.length ? s.customerClassifications : DEFAULT_CUSTOMER_CLASSIFICATIONS
  }
}

// ─── Formatos de moneda ──────────────────────────────────────────────────────

/** Busca la config de una moneda en el array de settings. */
export function getCurrencyConfig(currencies: CurrencyConfigType[], code: string): CurrencyConfigType | undefined {
  return currencies.find(c => c.code === code)
}

/** Retorna el símbolo de la moneda (ej: '$', 'US$', '€'). Fallback al código. */
export function getCurrencySymbol(currencies: CurrencyConfigType[], code: string): string {
  return getCurrencyConfig(currencies, code)?.symbol ?? code
}

/** Retorna el locale de la moneda para Intl.NumberFormat. */
export function getCurrencyLocale(currencies: CurrencyConfigType[], code: string): string {
  return getCurrencyConfig(currencies, code)?.locale ?? 'es-CO'
}

/** Formatea un valor monetario usando la config de moneda de la empresa. */
export function formatMoney(value: number | string | null | undefined, code: string, currencies: CurrencyConfigType[]): string {
  const num = Number(value ?? 0)
  if (!Number.isFinite(num)) return '0'
  const cfg = getCurrencyConfig(currencies, code)
  const decimals = cfg?.decimalDigits ?? 0
  const abs = Math.abs(num).toLocaleString(cfg?.locale ?? 'es-CO', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  })
  const text = `${num < 0 ? '-' : ''}${abs}`
  const symbol = cfg?.symbol ?? code
  return cfg?.symbolPosition === 'after' ? `${text} ${symbol}` : `${symbol}${text}`
}

// ─── Formato de fechas ───────────────────────────────────────────────────────

const pad = (n: number) => String(n).padStart(2, '0')

/** Aplica el formato de fecha de la empresa ('DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'). */
export function formatDate(value: Date | string | null | undefined, locale?: LocaleConfigType): string {
  if (!value) return '—'
  const date = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(date.getTime())) return '—'
  const fmt = locale?.dateFormat ?? 'DD/MM/YYYY'
  const dd = pad(date.getDate())
  const mm = pad(date.getMonth() + 1)
  const yyyy = String(date.getFullYear())
  switch (fmt) {
    case 'MM/DD/YYYY':
      return `${mm}/${dd}/${yyyy}`
    case 'YYYY-MM-DD':
      return `${yyyy}-${mm}-${dd}`
    default:
      return `${dd}/${mm}/${yyyy}`
  }
}

/** Fecha + hora con el locale de la empresa. */
export function formatDateTime(value: Date | string | null | undefined, locale?: LocaleConfigType): string {
  if (!value) return '—'
  const date = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(date.getTime())) return '—'
  const datePart = formatDate(date, locale)
  const timePart = date.toLocaleTimeString(locale?.dateLocale ?? 'es-CO', { hour: '2-digit', minute: '2-digit' })
  return `${datePart}, ${timePart}`
}

// ─── Helpers para selects ────────────────────────────────────────────────────

/** Opciones para el Select de moneda: { value: code, title: 'Peso Colombiano (COP)' }. */
export function currencyOptions(currencies: CurrencyConfigType[]) {
  return currencies.map(c => ({ value: c.code, title: `${c.name} (${c.code})` }))
}

/** Opciones para el Select de tipo de documento. */
export function documentTypeOptions(documentTypes: DocumentTypeConfigType[]) {
  return documentTypes.map(d => ({ value: d.code, title: d.label }))
}

/** Opciones para el Select de estado de cliente, ordenadas por `order`. */
export function customerStateOptions(states: CustomerStateConfigType[]) {
  return states
    .slice()
    .sort((a, b) => a.order - b.order)
    .map(s => ({ value: s.key, title: s.label }))
}

/** Mapa key → label de estados de cliente. */
export function customerStateLabels(states: CustomerStateConfigType[]): Record<string, string> {
  return Object.fromEntries(states.map(s => [s.key, s.label]))
}

/** Mapa key → color de estados de cliente (colores del Badge). */
export function customerStateColors(states: CustomerStateConfigType[]): Record<string, MainColorsType> {
  return Object.fromEntries(states.map(s => [s.key, (s.color as MainColorsType) ?? 'default']))
}

/** Opciones para el Select de tipo de cuenta bancaria. */
export function bankAccountTypeOptions(types: BankAccountTypeConfigType[]) {
  return types.map(t => ({ value: t.value, title: t.label }))
}

/** Opciones para el Select de etiqueta de dirección. */
export function addressLabelOptions(labels: AddressLabelConfigType[]) {
  return labels.map(l => ({ value: l.value, title: l.label }))
}

/** Opciones para el Select de clasificación de cliente. */
export function classificationOptions(classifications: CustomerClassificationConfigType[]) {
  return classifications
    .slice()
    .sort((a, b) => a.order - b.order)
    .map(c => ({ value: c.value, title: c.label }))
}
